import { __decorate } from "tslib";
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Validator } from 'simple-vue-validator';
import i18n from '@/locale';
import { getErrorsTemplate } from '@/helpers/translate';
import { validatorsToTemplateErrorMapping, isValid } from '@/components/forms/attributes/helpers';
let ValidationMixin = class ValidationMixin extends Vue {
    validationActiveArray = [];
    activateValidation() {
        return this.$validate();
    }
    getErrorTextByValidator(validator, value, schema) {
        const validatorValue = schema ? schema.validator[validator] : this.schema.validator[validator];
        const currentTempKey = validatorsToTemplateErrorMapping[validator];
        // @ts-ignore
        const errorsTemplate = getErrorsTemplate(this.$i18n.locale)[currentTempKey];
        return errorsTemplate.includes(`{0}`) ? errorsTemplate.replace(`{0}`, validatorValue) : errorsTemplate;
    }
    getValidatorErrors(validators, value, schema) {
        const hasValidator = Array.isArray(validators) && validators.length > 0;
        const errors = [];
        if (hasValidator) {
            validators.forEach((item) => {
                if (!this.isValid(value, item, schema ? schema.validator[item] : this.schema.validator[item])) {
                    errors.push(this.getErrorTextByValidator(item, value, schema));
                }
            });
        }
        return errors;
    }
    getValidatorFirstError(validators, value) {
        return this.getValidatorErrors(validators, value)[0] || null;
    }
    fieldValidator(value, validatorsList) {
        const validators = validatorsList;
        return () => {
            const error = this.getValidatorFirstError(validators, value);
            if (error) {
                return error;
            }
            return null;
        };
    }
    customValidator(value, validators) {
        return this.fieldValidator(value, validators);
    }
    addCurrentInputToValidateArray(fieldName) {
        if (!this.validationActiveArray.includes(fieldName)) {
            this.validationActiveArray.push(fieldName);
            this.activateValidation();
        }
    }
    fieldHasError(fieldName) {
        return Boolean(this.isValidationActivated &&
            this.validation.isTouched(`${fieldName}`) &&
            this.validationActiveArray.includes(fieldName) &&
            (this.serverErrors[fieldName] ||
                this.validation.hasError(`${fieldName}`) ||
                Boolean(this.validation.firstError(`${fieldName}`))));
    }
    fieldHasNotError(fieldName) {
        return Boolean(this.isValidationActivated &&
            this.validation.isTouched(`${fieldName}`) &&
            this.validationActiveArray.includes(fieldName) &&
            !(this.serverErrors[fieldName] || this.validation.hasError(`${fieldName}`)));
    }
    getFieldErrorText(fieldName) {
        if (!this.validationActiveArray.includes(fieldName)) {
            return null;
        }
        return this.serverErrors[fieldName] || this.validation.firstError(`${fieldName}`);
    }
    clearServerErrorsBase(fieldName) {
        this.serverErrors[fieldName] = null;
    }
    noNumbersAndCharactersMethod($event) {
        const a = /^[~`!@#$%\^&*()+=_\[\]\\';.,/{}|\\":<>\?]+$/i;
        if ((Number.isInteger(parseInt($event.key, 10)) &&
            $event.key !== '-') || a.test($event.key)) {
            $event.preventDefault();
        }
    }
    noSpacesMethod($event) {
        const target = $event.target;
        const currentValue = target.value;
        if (currentValue.length >= 100 &&
            !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes($event.key)) {
            $event.preventDefault();
            return;
        }
        const isSpace = $event.key === ' ' ||
            $event.code === 'Space' ||
            $event.keyCode === 32 ||
            $event.which === 32;
        if (isSpace) {
            $event.preventDefault();
            $event.stopPropagation();
            $event.stopImmediatePropagation();
            setTimeout(() => {
                if (target.value.includes(' ')) {
                    target.value = target.value.replace(/\s/g, '');
                    target.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }, 0);
        }
    }
    prepareServerFormErrors(data) {
        if (!data.errors) {
            return {};
        }
        function getFieldErrorNameFromString(errorType) {
            const subStringArray = errorType.split('.', 2);
            const index = subStringArray.indexOf('user');
            if (index !== -1)
                subStringArray.splice(index, 1);
            return subStringArray && subStringArray.length ? subStringArray[0] : null;
        }
        const errors = {};
        for (let i = 0; i < data.errors.length; i++) {
            const field = getFieldErrorNameFromString(data.errors[i].type);
            if (field) {
                errors[field] = data.errors[i].text;
            }
        }
        return errors;
    }
    // @ts-ignore-next-line
    emailValidator({ value, notRequired, requiredHint = i18n.t('VALIDATION.FIELD.REQUIRED') }) {
        return notRequired ? Validator.value(value).email() : Validator.value(value).required(requiredHint).email();
    }
    // @ts-ignore-next-line
    // eslint-disable-next-line max-len
    baseInputValidator({ value, minLength = 2, maxLength = 100, isRequired = true, requiredHint = i18n.t('VALIDATION.FIELD.REQUIRED') }) {
        if (isRequired) {
            return Validator.value(value).required(requiredHint).minLength(minLength).maxLength(maxLength);
        }
        return Validator.value(value).minLength(minLength).maxLength(maxLength);
    }
    // @ts-ignore-next-line
    baseTextAreaValidator({ value, minLength = 2 }) {
        return Validator.value(value).required().minLength(minLength).maxLength(500);
    }
    requiredValidator({ value }) {
        return Validator.value(value).required();
    }
    passwordValidator({ value }) {
        return Validator.value(value).required().minLength(6, i18n.tc(`VALIDATION.FIELD.PASSWORD.MIN`));
    }
    get isValidationActivated() {
        return this.validation && this.validation.activated;
    }
    get isFormValid() {
        if (this.validation && this.validation.errors.length !== 0) {
            return false;
        }
        if (this.requiredFields) {
            for (let i = 0; i < this.requiredFields.length; i++) {
                const key = this.requiredFields[i];
                const passedRecord = this.validation.passedRecords.find((item) => item.field === key);
                if (!passedRecord || passedRecord.value === false) {
                    return false;
                }
            }
            return true;
        }
        return !!(this.serverErrors && this.validation && this.validation.passedRecords.length === Object.keys(this.serverErrors).length);
    }
    propsKeyDownMethodOnlyNumbers($event, isPhonePrefixExist) {
        const availableKeys = [
            `Tab`,
            `Backspace`,
            `ArrowRight`,
            `ArrowLeft`,
        ];
        const currentInputValue = $event.target.value;
        const isPlusKey = $event.key === '+' || ($event.keyCode === 187 && $event.shiftKey);
        const isPlusAllowed = isPlusKey && currentInputValue.length === 0;
        if (isPlusAllowed) {
            return;
        }
        if (!Number.isInteger(parseInt($event.key, 10)) &&
            !availableKeys.includes($event.key) &&
            !isPlusKey &&
            !($event.ctrlKey && $event.key === 'v' || $event.key === 'c') &&
            !($event.metaKey && $event.key === 'v' || $event.key === 'c') ||
            (isPhonePrefixExist && parseInt($event.key, 10) === 0 && !currentInputValue.length)) {
            $event.preventDefault();
        }
    }
    isValid(value, validator, validatorValue) {
        return isValid(value, validator, validatorValue);
    }
};
ValidationMixin = __decorate([
    Component
], ValidationMixin);
export default ValidationMixin;
;
